import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import {
  EntityStatus,
  PropertyCadastralDto,
  PropertyConstructionPeriod,
  PropertyCreationStep,
  PropertyDpeTechnicalDto,
  PropertyDto,
} from '@workspace/data-types';
import { DtoChangedDetection, StepperComponent } from '@workspace/ng-aura';
import { finalize, Observable, of, switchMap } from 'rxjs';
import { PropertyCharacteristicsFormComponent } from '../property-characteristics/main/form/characteristics-form.component';
import { PropertyConstructionFormComponent } from '../property-construction/form/construction-form.component';
import { PropertyDpeFormComponent } from '../property-dpe/main/form/dpe-form.component';
import { PropertyFinancialsFormComponent } from '../property-financials/form/financials-form.component';
import { PropertyGeospatialDisplayComponent } from '../property-geospatial/display/geospatial-display.component';
import { PropertyKeyInfosFormComponent } from '../property-key-infos/main/form/key-infos-form.component';
import { PropertyPhotosFormComponent } from '../property-photos/form/photos-form.component';
import { PropertyPresentationFormComponent } from '../property-presentation/form/presentation-form.component';
import { PropertyCadastralStepComponent } from '../steps/property-cadastral-step/property-cadastral-step.component';
import { PropertyDefinitionStepComponent } from '../steps/property-definition-step/property-definition-step.component';
import { PropertyLocationStepComponent } from '../steps/property-location-step/property-location-step.component';
import { EtxeOwnerJourneyStepperComponent } from './owner-journey-stepper/owner-journey-stepper.component';
import { PropertyCreationStateService } from './services/property-creation-state.service';

@Component({
  selector: 'etxe-property-creation-container',
  imports: [
    CommonModule,
    StepperComponent,
    EtxeOwnerJourneyStepperComponent,
    MatButtonModule,
    MatIconModule,
    PropertyDefinitionStepComponent,
    PropertyLocationStepComponent,
    PropertyCadastralStepComponent,
    PropertyKeyInfosFormComponent,
    PropertyConstructionFormComponent,
    PropertyCharacteristicsFormComponent,
    PropertyDpeFormComponent,
    PropertyFinancialsFormComponent,
    PropertyPhotosFormComponent,
    PropertyPresentationFormComponent,
    PropertyGeospatialDisplayComponent,
    MatProgressSpinner,
  ],
  providers: [PropertyCreationStateService],
  templateUrl: './property-creation-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyCreationContainerComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly state = inject(PropertyCreationStateService);

  readonly initialProperty = input<PropertyDto | null>(null);

  // Delegated signals and state
  readonly property = this.state.property;
  readonly currentStep = this.state.currentStep;
  readonly loading = this.state.loading;
  readonly steps = this.state.steps;
  readonly computedSteps = this.state.computedSteps;
  readonly isCadastralConfirmed = this.state.isCadastralConfirmed;
  readonly isOwnerApp = this.state.isOwnerApp;

  readonly PropertyCreationStep = PropertyCreationStep;
  readonly PropertyConstructionPeriod = PropertyConstructionPeriod;
  readonly dpeFormComponent = viewChild(PropertyDpeFormComponent);

  private readonly queryParams = toSignal(this.route.queryParams);

  readonly canProceed = computed<boolean>(() => {
    const step = this.currentStep();
    if (this.state.isOptionalStep(step)) return true;

    if (step === PropertyCreationStep.DPE) {
      const dpeForm = this.dpeFormComponent();
      if (dpeForm && dpeForm.isDpeDone()) {
        if (dpeForm.isCostInvalid() || !dpeForm.hasAdemeData()) {
          return false;
        }
      }
      return this.state.currentStepFormValid();
    }

    if (step === PropertyCreationStep.CADASTRAL) {
      return this.state.isStepCompleted(step) && this.isCadastralConfirmed();
    }

    if (!this.state.currentStepFormValid()) return false;
    return this.state.isStepCompleted(step);
  });

  constructor() {
    // Sync initial property
    effect(() => {
      this.state.setInitialProperty(this.initialProperty());
    });

    // Sync Query Params and Current Step reactively
    effect(() => {
      const params = this.queryParams();
      if (!params) return;

      untracked(() => {
        const stepSlug = params['step'];
        const prop = this.property();
        const propId = prop?._id;

        // If on owner app and step points to mandate/documents/completed, redirect directly to dedicated pages
        if (this.isOwnerApp() && propId) {
          if (stepSlug === 'mandate' || (!stepSlug && prop?.farthestStepReached === 'mandate')) {
            void this.router.navigate(['/properties', propId, 'mandate']);
            return;
          }
          if (
            stepSlug === 'ownership-document' ||
            (!stepSlug && prop?.farthestStepReached === 'ownership-document')
          ) {
            void this.router.navigate(['/properties', propId, 'documents']);
            return;
          }
          if (stepSlug === 'completed' || (!stepSlug && prop?.farthestStepReached === 'completed')) {
            void this.router.navigate(['/properties', propId]);
            return;
          }
        }

        const index = this.state.stepSlugs.indexOf(stepSlug);
        const farthestIndex = this.state.stepSlugs.indexOf(prop?.farthestStepReached);

        if (index !== -1 && index <= PropertyCreationStep.FINANCIALS) {
          if (farthestIndex !== -1 && index > farthestIndex) {
            const maxAllowed = Math.min(
              PropertyCreationStep.FINANCIALS,
              Math.max(farthestIndex, this.state.getFirstUncompletedStepIndex()),
            );
            if (index > maxAllowed) {
              this.navigateToStep(maxAllowed as PropertyCreationStep);
              return;
            }
          }
          if (this.currentStep() !== (index as PropertyCreationStep)) {
            this.state.setCurrentStep(index as PropertyCreationStep);
          }
        } else {
          const maxStepIndex = PropertyCreationStep.FINANCIALS;
          const defaultStep =
            farthestIndex !== -1 ? Math.min(farthestIndex, maxStepIndex) : PropertyCreationStep.DEFINITION;
          this.navigateToStep(defaultStep as PropertyCreationStep);
        }
      });
    });

    // Scroll to top whenever step changes
    effect(() => {
      this.currentStep();
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const scrollableContainers = document.querySelectorAll(
          'main, .overflow-y-auto, [data-scroll-container]',
        );
        scrollableContainers.forEach((el) => el.scrollTo({ top: 0, behavior: 'smooth' }));
      });
    });
  }

  onStepValidityChanged(valid: boolean): void {
    this.state.setStepValidity(valid);
  }

  onCadastralUpdated(cadastral: PropertyCadastralDto): void {
    this.state.onCadastralUpdated(cadastral);
  }

  onCadastralConfirmationChanged(confirmed: boolean): void {
    this.state.setCadastralConfirmed(confirmed);
  }

  onDpeTechnicalExtracted(technical: PropertyDpeTechnicalDto): void {
    this.state.onDpeTechnicalExtracted(technical);
  }

  onGeospatialRefreshed(refreshedProperty: PropertyDto): void {
    this.state.onGeospatialRefreshed(refreshedProperty);
  }

  onStepDtoChanged(step: PropertyCreationStep, event: DtoChangedDetection<unknown>): void {
    this.state.onStepDtoChanged(step, event);
  }

  isStepCompleted(step: PropertyCreationStep): boolean {
    return this.state.isStepCompleted(step);
  }

  onStepSelect(index: number): void {
    const step = index as PropertyCreationStep;
    const current = this.currentStep();
    const farthestIndex = this.state.stepSlugs.indexOf(this.property()?.farthestStepReached);

    if (step > current && !this.canProceed()) {
      return;
    }

    if (step <= current || (farthestIndex !== -1 && step <= farthestIndex)) {
      this.navigateToStep(step);
      return;
    }

    let canJump = true;
    for (let i = current; i < step; i++) {
      if (!this.state.isStepCompleted(i as PropertyCreationStep)) {
        canJump = false;
        break;
      }
    }

    if (canJump) {
      this.navigateToStep(step);
    }
  }

  nextStep(): void {
    if (this.loading() || this.currentStep() >= this.steps().length || !this.canProceed()) return;

    this.state.loading.set(true);
    const next = this.currentStep() + 1;
    this.state.advanceFarthestStep(next);

    this.state
      .persistStep$(this.currentStep())
      .pipe(
        switchMap(() => {
          if (
            this.currentStep() === this.steps().length - 1 &&
            this.property().state.status === EntityStatus.DRAFT
          ) {
            return this.finish();
          }
          this.navigateToStep(next);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.state.loading.set(false)),
      )
      .subscribe();
  }

  previousStep(): void {
    if (this.currentStep() > 0) {
      this.navigateToStep(this.currentStep() - 1);
    }
  }

  finish(): Observable<unknown> {
    const prop = this.property();
    this.state.clearDraft();

    if (this.isOwnerApp()) {
      void this.router.navigate(['/properties', prop._id, 'mandate']);
      return of(null);
    }

    void this.router.navigate(['/properties', prop._id, 'edit']);
    return of(null);
  }

  private navigateToStep(step: PropertyCreationStep): void {
    const propId = this.property()?._id;

    if (this.isOwnerApp() && propId) {
      if (step === PropertyCreationStep.MANDATE) {
        void this.router.navigate(['/properties', propId, 'mandate']);
        return;
      }
      if (step === PropertyCreationStep.OWNERSHIP_DOCUMENT) {
        void this.router.navigate(['/properties', propId, 'documents']);
        return;
      }
      if (step === PropertyCreationStep.COMPLETED) {
        void this.router.navigate(['/properties', propId]);
        return;
      }
    }

    const maxCreationStep = PropertyCreationStep.FINANCIALS;
    const clampedStep = Math.min(step, maxCreationStep);

    if (clampedStep >= 0 && clampedStep < this.state.stepSlugs.length) {
      const stepSlug = this.state.stepSlugs[clampedStep];

      if (propId) {
        void this.router.navigate(['/properties', propId, 'creation'], {
          queryParams: { step: stepSlug },
          queryParamsHandling: 'merge',
        });
      } else {
        void this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { step: stepSlug },
          queryParamsHandling: 'merge',
        });
      }
      this.state.setCurrentStep(clampedStep);
    }
  }
}
